/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import { useContext } from 'react';

import { InlineNotification, Typography, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import WorkEligibilitySkeletonLoader from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-eligibility/WorkEligibilitySkeletonLoader';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  customCss?: Interpolation<Theme>;
};

type Props = PropsFromCaller;

const styles = {
  sectionTitle: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  bodyText: () => css`
    color: var(--cds-color-grey-600);
  `,
  aboutText: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
  emptyStateText: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  editButton: css`
    position: absolute;
    top: 5px;
    right: 5px;
  `,
};

const WorkEligibilitySection = ({ customCss }: Props) => {
  const { profileState = {}, isProfileLoading, isRecruiter } = useContext(ProfileContext);
  const { countryWorkEligibilities } = profileState;

  if (isProfileLoading) {
    return (
      <ProfileViewerCard customCss={customCss}>
        <WorkEligibilitySkeletonLoader />
      </ProfileViewerCard>
    );
  }

  if (
    !isRecruiter ||
    (isRecruiter &&
      !countryWorkEligibilities?.US?.hasWorkAuthorization &&
      !countryWorkEligibilities?.US?.requiresVisaSponsorship)
  ) {
    return null;
  }

  return (
    <ProfileViewerCard customCss={customCss}>
      <Typography variant="h2semibold" data-testid="work-eligibility-section-title" css={styles.sectionTitle}>
        {_t('Work eligibility')}
      </Typography>
      {countryWorkEligibilities?.US?.hasWorkAuthorization && (
        <Typography2 component="p" css={[styles.aboutText, styles.bodyText]} variant="bodyPrimary" color="inherit">
          {_t('Authorized to work in the U.S.')}
        </Typography2>
      )}
      {countryWorkEligibilities?.US?.requiresVisaSponsorship && (
        <Typography2 component="p" css={styles.bodyText} variant="bodyPrimary" color="inherit">
          {_t('Requires sponsorship for an employment visa')}
        </Typography2>
      )}
    </ProfileViewerCard>
  );
};

const ErrorBoundWorkEligibilitySection = (props: Props) => {
  return (
    <ErrorBoundaryWithTagging
      componentName="WorkEligibilitySection"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the work eligibility section. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <WorkEligibilitySection {...props} />
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundWorkEligibilitySection;
